<template>
  <div v-if="$apollo.queries.event.loading">
    <v-container>
      <AppLoadingPage />
    </v-container>
  </div>
  <div v-else-if="!event || !event.id">
    <NoPage custom-text="Could not find the event from server" />
  </div>
  <div v-else>
    <div v-if="showAlert" class="px-0" data-test="eventAlert">
      <AppAlertNotice :text="event.alertText" />
    </div>

    <v-container
      class="container-content pa-0 event-profile-page"
      style="padding-top: 0 !important; padding-bottom: 0"
    >
      <EventCardLarge :event="event" />
    </v-container>
    <v-toolbar
      id="jumpEventPage"
      class="event-selections__wrapper mt-4"
      color="white"
      :height="$vuetify.display.mdAndUp ? 80 : 46"
      tag="nav"
    >
      <v-container
        class="mx-auto py-0 my-0"
        style="height: 100%; padding-top: 0 !important"
      >
        <v-toolbar-items>
          <div class="event-selections">
            <div>
              <router-link
                :to="{
                  name: 'EventSchedule',
                  params: { eventId: event.id }
                }"
                :class="[
                  currentPage('EventSchedule'),
                  'event-link',
                  'no-default-link-style'
                ]"
                data-test="schedule"
              >
                <div>
                  <v-icon>$info</v-icon>
                  <div class="text-caption caption-tabs">
                    {{
                      $t(
                        "events:EventPage.Schedule.navigation",
                        "Schedule",
                        "Link to schedule section in event page"
                      )
                    }}
                  </div>
                </div>
              </router-link>
            </div>
            <div>
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-col
                    v-bind="props"
                    :class="[
                      !event.startingListUrl ? 'disabled' : '',
                      'event-link',
                      'no-default-link-style'
                    ]"
                    :href="startingListUrl"
                    style="position: relative; pointer-events: initial"
                    target="_blank"
                    :tag="!event.startingListUrl ? 'button' : 'a'"
                    data-test="startinglist"
                  >
                    <div>
                      <v-icon>$iconClipboard</v-icon>
                      <div class="text-caption caption-tabs">
                        {{
                          $t(
                            "events:EventPage.StartingList.navigation",
                            "Starting list",
                            "Link to starting list in event page"
                          )
                        }}
                      </div>
                    </div>
                  </v-col>
                </template>
                <span>{{ $t("events:EventPage.StartingList.tooltip") }}</span>
              </v-tooltip>
            </div>
            <div>
              <router-link
                :to="{
                  name: 'EventResultsPage',
                  params: { eventId: event.id }
                }"
                :class="[
                  currentPage(
                    $route.name == 'EventResultsPage' ? 'EventResultsPage' : ''
                  ),
                  'event-link',
                  'no-default-link-style'
                ]"
                data-test="results"
              >
                <div>
                  <v-icon>$iconTrophy</v-icon>
                  <div class="text-caption caption-tabs">
                    {{
                      $t(
                        "events:EventPage.Results.navigation",
                        "Results",
                        "Link to results section in event page"
                      )
                    }}
                  </div>
                </div>
              </router-link>
            </div>

            <div>
              <template v-if="venueExists">
                <router-link
                  :to="{
                    name: 'EventMap',
                    params: { eventId: event.id }
                  }"
                  :class="[
                    currentPage('EventMap'),
                    'event-link',
                    'no-default-link-style'
                  ]"
                  data-test="venue"
                >
                  <div>
                    <v-icon>$iconMap</v-icon>
                    <div class="text-caption caption-tabs">
                      {{ $t("events:EventCard.venue.label") }}
                    </div>
                  </div>
                </router-link>
              </template>
              <v-tooltip v-else location="bottom">
                <template #activator="{ props: attrs }">
                  <div
                    v-bind="attrs"
                    class="disabled event-link no-default-link-style"
                    style="position: relative; pointer-events: initial"
                    data-test="venue"
                  >
                    <div>
                      <v-icon>$iconMap</v-icon>
                      <div class="text-caption caption-tabs">
                        {{ $t("events:EventCard.venue.label") }}
                      </div>
                    </div>
                  </div>
                </template>
                <span>{{
                  $t("events:EventCard.venueNotPresent.tooltip")
                }}</span>
              </v-tooltip>
            </div>

            <div>
              <v-col
                :class="[
                  !event.broadcastUrl ? 'disabled' : '',
                  'event-link',
                  'no-default-link-style'
                ]"
                :href="broadcastUrl"
                style="pointer-events: initial"
                target="_blank"
                data-test="broadcast"
                :tag="!event.broadcastUrl ? 'button' : 'a'"
              >
                <div>
                  <v-icon>$iconRecorder</v-icon>
                  <div class="text-caption caption-tabs">
                    {{
                      $t(
                        "events:EventPage.Live.navigation",
                        "Live",
                        "Link to live stream in event page"
                      )
                    }}
                  </div>
                </div>
              </v-col>
            </div>
            <div>
              <v-col
                :class="[
                  !event.homepageUrl ? 'disabled' : '',
                  'event-link',
                  'no-default-link-style'
                ]"
                :href="homepageUrl"
                target="_blank"
                style="pointer-events: initial"
                :tag="!event.homepageUrl ? 'button' : 'a'"
                data-test="homepage"
              >
                <div>
                  <v-icon>$iconGlobe</v-icon>
                  <div class="text-caption caption-tabs">
                    {{
                      $t(
                        "events:EventPage.Homepage.navigation",
                        "Homepage",
                        "Link to homepage in event page"
                      )
                    }}
                  </div>
                </div>
              </v-col>
            </div>
          </div>
        </v-toolbar-items>
      </v-container>
    </v-toolbar>

    <router-view />
  </div>
</template>

<script>
import { ref, computed } from "vue";
import gql from "fraql";
import i18next from "i18next";
import { useEventUrlHelpers } from "@/components/event/mixins/eventUrlMixin";
import EventCardLarge from "./EventCardLarge/index.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";
import { DisplayTextFragments } from "@composables/display_text";
import NoPage from "@/components/error_pages/404.vue";
import { checkVenueExists } from "./venueExists.ts";
import FipaListener from "@/utils/listenerUtility";
import { SentryCaptureError } from "@/utils/sentryErrorHandler";

export const EVENT_QUERY = gql`
  query eventProfile($id: ID, $locale: String) {
    event(id: $id) {
      id
      name
      noncompetition
      ${EventCardLarge.fragments.eventFields}
      description
      startDate
      endDate
      isHighlighted
      registrationEndDate
      alertText

      venueLocationLatitude
      venueLocationLongitude
      venueAddress

      administrativeAlertMessages {
        ${DisplayTextFragments.translation}
      }
      cups {
        id
        ${EventCardLarge.fragments.cups}
      }
      
      regionId
      region @client {
        id
        name
      }
      countryId
      country @client {
        id
        name
        alpha2
        ioc
      }
      organiser {
        id
        name
        logoPath
        addressCountryId
        addressCity
        addressIndex
        addressText
        regionId
        region @client {
          id
          name
        }
      }
      notes {
        id
        content
        createdByPerson {
          id
          name
        }
        relatedAction
        createdAt
      }
      
      competitionLevels {
        id
        name
      }
      eventProducts {
        id
        paymentStructure
      }
      competitions {
        id
        name
        code
        cupSeasons {
          id
          ${EventCardLarge.fragments.cupSeason}
        }
        eventcodeId
        difficultyLevelId
        difficultyLevel @client {
          id
          name
          displayName
        }
        competitionRuleId
        competitionRule @client {
          id
          name
        }
        competitionLevelId
        competitionLevel @client {
          id
          name
        }
      }
      place
      startingListUrl
      broadcastUrl
      homepageUrl
      allowedMutations
      allowedFields
      useOnlinePaymentsForPrizeMoney
      useOnlinePayments
      useEquipe
      equipeAuthData(locale: $locale) {
        ${EventCardLarge.fragments.equipeAuthData}
      }
    }
  }
`;

export default {
  name: "EventProfilePage",
  components: {
    EventCardLarge,
    AppAlertNotice,
    AppLoadingPage,
    NoPage
  },
  props: {
    eventId: {
      type: String,
      default: null
    }
  },
  setup() {
    const event = ref({ id: null });
    const { homepageUrl, broadcastUrl, startingListUrl } =
      useEventUrlHelpers(event);

    const showAlert = computed(() => {
      return event.value.alertText && event.value.alertText != "<p></p>"
        ? true
        : false;
    });

    return {
      showAlert,
      event,
      homepageUrl,
      broadcastUrl,
      startingListUrl
    };
  },
  apollo: {
    event: {
      query: EVENT_QUERY,
      variables() {
        return {
          id: this.eventId,
          locale: i18next.language
        };
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;
        if (data && (!data.event || !data.event.id)) {
          SentryCaptureError(Error("Event was not found"), {
            level: "info",
            additionalDetails: {
              eventId: this.eventId,
              query: "eventProfile"
            }
          });
        } else if (data && data.event) {
          this.event = data.event;
          if (data.event.noncompetition) {
            SentryCaptureError(
              Error(
                "Page is for normal event but server returned noncompetition event"
              ),
              {
                level: "warning",
                additionalDetails: {
                  eventId: this.eventId,
                  query: "EventProfile"
                }
              }
            );

            this.$router.replace({
              name: "NoncompetitionEventProfilePage",
              params: {
                eventId: data.event.id
              }
            });
            return;
          }
          FipaListener.emit("breadcrumbPageName", {
            name: data.event.name,
            id: data.event.id,
            breadcrumbName: "eventName"
          });
        }
      },
      fetchPolicy: "cache-first"
    }
  },
  computed: {
    venueExists() {
      return checkVenueExists(this.event);
    }
  },
  methods: {
    currentPage(routeName) {
      return this.$route.name == routeName ? "active" : undefined;
    }
  }
};
</script>
<style lang="scss">
#jumpEventPage {
  .v-toolbar__content {
    padding: 0;
  }
}

.event-selections__wrapper {
  width: 100%;

  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2) !important;
  background-color: rgb(var(--v-theme-light-grey));
  @media (max-width: 960px) {
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
    background-color: rgb(var(--v-theme-light-grey)) !important;
  }

  .v-toolbar__content {
    overflow: visible;
  }
  .event-selections {
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;

    a.event-link {
      color: black !important;
      text-decoration: none;
      div.caption {
        min-width: 8.5rem;
      }

      &:hover,
      &:focus {
        color: black !important;
        text-decoration: none;
      }
    }

    & > div {
      display: flex;
      justify-content: center;
    }

    .event-link {
      position: relative;
      height: 75px;
      //transform: translateY(33px);
      transform: translateY(5px);

      text-align: center;
      border-radius: 5px 5px 0 0;
      border: 1px solid transparent;
      border-bottom: none;
      padding: 21px 0;
      min-width: 120px;
      margin: 0 21px;
      display: flex;
      justify-content: center;
      align-items: center;

      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

      svg {
        transform: translateY(3px);
      }

      @media (max-width: 960px) {
        transform: translateY(0);
        height: 46px;
      }

      &:hover,
      &.active {
        border-radius: 5px 5px 0 0;
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-bottom: none;
        z-index: 1;
      }

      &:hover {
        &::after {
          content: " ";
          position: absolute;
          background: rgb(var(--v-theme-background));
          width: 100%;
        }
      }

      &.active {
        background: rgb(var(--v-theme-background));

        &::after {
          content: " ";
          position: absolute;
          background: rgb(var(--v-theme-background));
          width: 100%;
          height: 5px;
          bottom: -2px;
        }
      }

      &:focus {
        background: rgba(185, 185, 185, 0.15);
      }

      &.disabled {
        pointer-events: none;
        color: grey;
        opacity: 0.5;
        cursor: not-allowed;
        &:focus {
          background: rgba(185, 185, 185, 0.15);
        }
      }

      @media (max-width: 960px) {
        min-width: 3.2em;
        margin: 0 6px;
        &:not(.disabled) {
          opacity: 1;
          .v-icon {
            color: black !important;
          }
        }
      }
    }
  }
}
</style>
